// Pure token-shape migration (no browser/Supabase deps so it's unit-testable).
// Converts OLD-shape token objects (pre-TokenSchema: fontFamily/fontSize/
// fontWeight, button background/color, focus outline; pre-0.8.0 SpaceValue grid
// and CSS-shorthand button padding) to the current @alchmystudio/config shape
// (family/size/weight + required letterSpacing, button bg/text, focus ring; px-number
// grid anchors, button padding { y, x }). Idempotent — new-shape input passes
// through. Applied when seeding the token editor so a client whose last build stored
// old-shape tokens still produces a VALID build input (no "Invalid build input").

import { lengthToPx } from './units.ts';

function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

// Flatten a length to a px number: number stays, rem/px string → px, and an old
// fluid { min, max } pair collapses to its max (the upper/desktop anchor).
function toPxNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return lengthToPx(v);
  const o = asObj(v);
  if (o && o.max !== undefined) return toPxNumber(o.max);
  return undefined;
}

// Old CSS-shorthand padding string ('0.75rem 1.5rem') → { y, x } px numbers.
function migratePadding(v: unknown): unknown {
  if (typeof v !== 'string') return v; // already { y, x } (or absent)
  const parts = v
    .trim()
    .split(/\s+/)
    .map((p) => lengthToPx(p))
    .filter((n): n is number => n !== undefined);
  if (parts.length === 0) return undefined;
  return { y: parts[0], x: parts.length > 1 ? parts[1] : parts[0] };
}

// Old SpaceValue grid (gutter/margin/maxWidth as strings or { min, max }) → px
// numbers; drops the dropped mobile.maxWidth.
function migrateGrid(v: unknown): unknown {
  const o = asObj(v);
  if (!o) return v;
  const out: Record<string, unknown> = { ...o };
  for (const k of ['gutter', 'margin', 'maxWidth'] as const) {
    if (o[k] !== undefined) out[k] = toPxNumber(o[k]) ?? o[k];
  }
  const mob = asObj(o.mobile);
  if (mob) {
    const m: Record<string, unknown> = { ...mob };
    for (const k of ['gutter', 'margin'] as const) {
      if (mob[k] !== undefined) m[k] = toPxNumber(mob[k]) ?? mob[k];
    }
    delete m.maxWidth; // removed in 0.8.0
    out.mobile = m;
  }
  return out;
}

function mapFamily(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  return v === 'heading' || v === 'body' || v === 'accent' ? `var(--font-${v})` : v;
}

function migrateTextStyle(v: unknown): unknown {
  const o = asObj(v);
  if (!o) return v;
  const out: Record<string, unknown> = { ...o };
  if (out.family === undefined && o.fontFamily !== undefined) out.family = mapFamily(o.fontFamily);
  if (out.size === undefined && o.fontSize !== undefined) out.size = o.fontSize;
  if (out.weight === undefined && o.fontWeight !== undefined) out.weight = o.fontWeight;
  if (out.letterSpacing === undefined) out.letterSpacing = '0'; // required by TokenSchema
  delete out.fontFamily;
  delete out.fontSize;
  delete out.fontWeight;
  return out;
}

function migrateButtonState(v: unknown): unknown {
  const o = asObj(v);
  if (!o) return v;
  const out: Record<string, unknown> = { ...o };
  if (out.bg === undefined && o.background !== undefined) out.bg = o.background;
  if (out.text === undefined && o.color !== undefined) out.text = o.color;
  delete out.background;
  delete out.color;
  return out;
}

function extractColor(s: string): string {
  const m = s.match(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-zA-Z]+)\s*$/);
  return m ? m[1] : s;
}

function migrateFocus(v: unknown): unknown {
  const o = asObj(v);
  if (!o) return v;
  if (o.ring !== undefined) return { ring: o.ring };
  if (typeof o.outline === 'string') return { ring: extractColor(o.outline) };
  return o;
}

export function migrateTokens(tokens: unknown): unknown {
  const t = asObj(tokens);
  if (!t) return tokens;
  const out: Record<string, unknown> = { ...t };

  const typo = asObj(t.typography);
  const styles = typo && asObj(typo.textStyles);
  if (typo && styles) {
    const migrated: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(styles)) migrated[k] = migrateTextStyle(v);
    out.typography = { ...typo, textStyles: migrated };
  }

  const button = asObj(t.button);
  if (button) {
    const padding = button.padding !== undefined ? migratePadding(button.padding) : undefined;
    out.button = {
      ...button,
      ...(button.default !== undefined ? { default: migrateButtonState(button.default) } : {}),
      ...(button.hover !== undefined ? { hover: migrateButtonState(button.hover) } : {}),
      ...(button.pressed !== undefined ? { pressed: migrateButtonState(button.pressed) } : {}),
      ...(button.focus !== undefined ? { focus: migrateFocus(button.focus) } : {}),
      ...(padding !== undefined ? { padding } : {}),
    };
    if (padding === undefined) delete (out.button as Record<string, unknown>).padding;
  }

  if (t.grid !== undefined) out.grid = migrateGrid(t.grid);

  return out;
}
